import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDate, formatErrorDetail, formatPHP } from "@/lib/api";
import { Empty, PageHeader, Stat, Surface, TableWrap } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import {
    BriefcaseBusiness,
    CalendarDays,
    CheckCircle2,
    Copy,
    FileText,
    Globe,
    Mail,
    MapPin,
    Phone,
    Plus,
    Share2,
    Sparkles,
    Upload,
    UserRound,
    UsersRound,
    X,
} from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const STAGES = ["applied", "screening", "interview", "assessment", "offer", "hired", "rejected", "talent_pool"];

const stageTone = {
    applied: "bg-sky-50 text-sky-700 border-sky-200",
    screening: "bg-violet-50 text-violet-700 border-violet-200",
    interview: "bg-blue-50 text-blue-700 border-blue-200",
    assessment: "bg-amber-50 text-amber-700 border-amber-200",
    offer: "bg-rose-50 text-rose-700 border-rose-200",
    hired: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-slate-100 text-slate-700 border-slate-200",
    talent_pool: "bg-cyan-50 text-cyan-700 border-cyan-200",
};

const titleCase = (s) => String(s || "—").replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());
const initials = (c) => `${c?.first_name?.[0] || ""}${c?.last_name?.[0] || ""}`.toUpperCase() || "C";

// A candidate is "matched" if the public apply flagged it, or the resume match
// clears the same threshold (required skills substantially met).
const MATCH_THRESHOLD = 50;
const isMatched = (c) => Boolean(c?.matched) || Number(c?.resume_match?.match_score || 0) >= MATCH_THRESHOLD;

function SourceBadge({ c }) {
    const src = c?.source;
    if (!src || src === "manual") return null;
    const label = src === "referral"
        ? (c.referrer_name ? `Referral · ${c.referrer_name}` : "Referral")
        : titleCase(src);
    const tone = src === "referral"
        ? "bg-indigo-50 text-indigo-700 border-indigo-200"
        : "bg-slate-50 text-slate-600 border-slate-200";
    return <span className={`text-[10px] px-2 py-0.5 rounded-full border ${tone}`}>{label}</span>;
}

function SkillPill({ children, tone = "default" }) {
    const cls = tone === "good"
        ? "bg-emerald-100 text-emerald-800"
        : tone === "warn"
            ? "bg-amber-100 text-amber-800"
            : tone === "blue"
                ? "bg-blue-100 text-blue-800"
                : "bg-secondary text-foreground";
    return <span className={`text-[10px] px-2 py-1 rounded-md ${cls}`}>{children}</span>;
}

// Linear hiring flow the ◀ ▶ card buttons walk through (rejected/talent_pool
// are parking lanes — move there from the candidate profile).
const FLOW = ["applied", "screening", "interview", "assessment", "offer", "hired"];

function CandidateCard({ c, job, openProfile, onMove }) {
    const matched = isMatched(c);
    const score = c?.resume_match?.match_score;
    const idx = FLOW.indexOf(c.stage);
    const prev = idx > 0 ? FLOW[idx - 1] : null;
    const next = idx >= 0 && idx < FLOW.length - 1 ? FLOW[idx + 1] : null;
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => openProfile(c)}
            className={`w-full text-left rounded-2xl border p-4 shadow-sm hover:shadow-md transition cursor-pointer ${
                matched ? "bg-emerald-50/60 border-emerald-300 hover:border-emerald-400" : "bg-white hover:border-primary/50"
            }`}
            data-testid={`cand-card-${c.id}`}
        >
            <div className="flex items-center gap-3">
                <div className={`size-11 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                    matched ? "bg-emerald-600 text-white" : "bg-primary/10 text-primary"}`}>
                    {initials(c)}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-foreground truncate flex items-center gap-1.5">
                        {c.first_name} {c.last_name}
                        {matched && <CheckCircle2 className="size-4 text-emerald-600 shrink-0" data-testid={`cand-matched-${c.id}`} />}
                    </div>
                    <div className="text-xs text-muted-foreground leading-snug line-clamp-2 mt-0.5">
                        {c.current_title || job?.title || "Candidate"}
                        {c.current_company ? ` · ${c.current_company}` : ""}
                    </div>
                </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {score != null && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
                        matched ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-secondary text-muted-foreground border-border"}`}>
                        {score}% match
                    </span>
                )}
                <SourceBadge c={c} />
            </div>
            {onMove && (prev || next) && (
                <div className="mt-3 pt-2 border-t border-border/60 space-y-1">
                    {next && (
                        <button type="button" className="w-full h-8 rounded-lg bg-primary text-white text-[11px] font-semibold flex items-center justify-center gap-1 truncate"
                            onClick={(e) => { e.stopPropagation(); onMove(c.id, next); }} data-testid={`move-next-${c.id}`}>
                            Move to {titleCase(next)} ▶
                        </button>
                    )}
                    {prev && (
                        <button type="button" className="w-full h-7 rounded-lg border border-border text-[11px] text-muted-foreground hover:border-primary hover:text-primary flex items-center justify-center gap-1 truncate"
                            onClick={(e) => { e.stopPropagation(); onMove(c.id, prev); }} data-testid={`move-prev-${c.id}`}>
                            ◀ Back to {titleCase(prev)}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function CopyRow({ label, url, testid }) {
    const copy = () => {
        navigator.clipboard?.writeText(url).then(
            () => toast.success(`${label} copied`),
            () => toast.error("Copy failed — select and copy manually"),
        );
    };
    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <div className="flex items-center gap-2 mt-1">
                <Input readOnly value={url} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid={testid} />
                <button type="button" onClick={copy} className="h-9 px-3 rounded-md bg-secondary text-sm inline-flex items-center gap-1 shrink-0" data-testid={`${testid}-copy`}>
                    <Copy className="size-4" /> Copy
                </button>
            </div>
        </div>
    );
}

function JobPhotoSection({ requisition, info, refresh }) {
    // Optional photo — makes the posting look good when shared on social media.
    const [photoUrl, setPhotoUrl] = useState(null);
    const [busy, setBusy] = useState(false);
    const fileRef = React.useRef(null);

    useEffect(() => {
        let revoked = null;
        if (info?.has_photo) {
            api.get(`/recruitment/requisitions/${requisition.id}/photo`, { responseType: "blob" })
                .then((r) => { revoked = URL.createObjectURL(r.data); setPhotoUrl(revoked); })
                .catch(() => setPhotoUrl(null));
        } else setPhotoUrl(null);
        return () => { if (revoked) URL.revokeObjectURL(revoked); };
    }, [info?.has_photo, requisition.id]);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/recruitment/requisitions/${requisition.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Photo attached to this job posting");
            refresh();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Upload failed"); }
        finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const remove = async () => {
        setBusy(true);
        try { await api.delete(`/recruitment/requisitions/${requisition.id}/photo`); toast.success("Photo removed"); refresh(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="rounded-lg border p-3">
            <div className="flex items-center justify-between">
                <div>
                    <Label className="text-xs">Photo for social media (optional)</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Shown on the public careers board and the job page — great for shared links.</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
                        className="h-8 px-3 rounded-md bg-secondary text-xs" data-testid={`job-photo-upload-${requisition.id}`}>
                        {info?.has_photo ? "Replace" : "Add photo"}
                    </button>
                    {info?.has_photo && (
                        <button type="button" disabled={busy} onClick={remove} className="h-8 px-3 rounded-md text-xs text-destructive border border-destructive/30">
                            Remove
                        </button>
                    )}
                </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            {photoUrl && (
                <img src={photoUrl} alt="Job posting" className="mt-2 w-full max-h-44 object-cover rounded-md border" data-testid={`job-photo-preview-${requisition.id}`} />
            )}
        </div>
    );
}

function ShareDialog({ requisition, onChanged }) {
    const [open, setOpen] = useState(false);
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    const fetchInfo = async () => {
        try {
            const { data } = await api.get(`/recruitment/requisitions/${requisition.id}/share`);
            setInfo(data);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load share info"); }
    };

    useEffect(() => { if (open) fetchInfo(); /* eslint-disable-next-line */ }, [open]);

    const setPublish = async (publish) => {
        setBusy(true);
        try {
            await api.post(`/recruitment/requisitions/${requisition.id}/${publish ? "publish" : "unpublish"}`);
            toast.success(publish ? "Job published" : "Job unpublished");
            await fetchInfo();
            onChanged?.();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };

    // The company name the posting shows. Picked from the group's companies before posting, and
    // changeable afterwards (the live job reads it, so the board updates at once).
    const [names, setNames] = useState(null);
    const [chosen, setChosen] = useState(requisition.posting_company_name || "");
    useEffect(() => {
        if (!open || names) return;
        api.get("/recruitment/posting-names").then((r) => setNames(r.data)).catch(() => setNames({ default: "", options: [] }));
    }, [open, names]);
    useEffect(() => { setChosen(requisition.posting_company_name || ""); }, [requisition.posting_company_name]);
    const saveName = async (value) => {
        setChosen(value);
        try {
            await api.patch(`/recruitment/requisitions/${requisition.id}`, { posting_company_name: value });
            toast.success(value ? `The posting will show ${value}` : `The posting will show ${names?.default || "the company name"}`);
            onChanged?.();
        } catch (e) {
            setChosen(requisition.posting_company_name || "");
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not change the name");
        }
    };
    const postingNameField = (
        <div className="text-left">
            <Label className="text-xs">Company name shown on the posting</Label>
            <select className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={chosen} onChange={(e) => saveName(e.target.value)} disabled={!names} data-testid={`req-posting-name-${requisition.id}`}>
                <option value="">{names?.default ? `${names.default} (this requisition's company)` : "This requisition's company"}</option>
                {(names?.options || []).filter((n) => n !== names?.default).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">Applicants see this name on the job page and the careers board. It is saved as soon as you pick it.</p>
        </div>
    );

    const token = info?.public_token;
    const publicUrl = token ? `${origin}/careers/${token}` : "";
    const referralUrl = token && info?.referral_code ? `${publicUrl}?ref=${info.referral_code}` : "";
    const tagged = (tag) => `${publicUrl}?ref=${tag}`;

    const socials = [
        { key: "facebook", label: "Facebook", href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
        { key: "linkedin", label: "LinkedIn", href: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}` },
        { key: "x", label: "X / Twitter", href: (u) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent("We're hiring: " + requisition.title)}` },
    ];

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button className="text-xs text-primary inline-flex items-center gap-1" data-testid={`req-share-${requisition.id}`}>
                    <Share2 className="size-3.5" /> {requisition.is_published ? "Share" : "Publish"}
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Globe className="size-5" /> Share “{requisition.title}”</DialogTitle></DialogHeader>

                {!info ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
                ) : !info.is_published ? (
                    <div className="py-4 space-y-3">
                        <p className="text-sm text-muted-foreground text-center">Publish this job to a public careers page you can share on social media and via employee referral links.</p>
                        {postingNameField}
                        <JobPhotoSection requisition={requisition} info={info} refresh={fetchInfo} />
                        <div className="text-center">
                            <button disabled={busy} className="h-9 px-4 rounded-md bg-primary text-white text-sm inline-flex items-center gap-2" onClick={() => setPublish(true)} data-testid={`req-publish-${requisition.id}`}>
                                <Globe className="size-4" /> Publish Job
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <CopyRow label="Public job link (company / social media)" url={publicUrl} testid={`share-public-${requisition.id}`} />

                        {postingNameField}

                        <JobPhotoSection requisition={requisition} info={info} refresh={fetchInfo} />

                        <div>
                            <Label className="text-xs">Share to social media</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {socials.map((s) => (
                                    <a key={s.key} href={s.href(tagged(s.key))} target="_blank" rel="noreferrer"
                                        className="h-9 px-3 rounded-md bg-secondary text-sm inline-flex items-center gap-1" data-testid={`share-social-${s.key}-${requisition.id}`}>
                                        <Share2 className="size-3.5" /> {s.label}
                                    </a>
                                ))}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">Each button tags applicants with its source so you can see where hires come from.</p>
                        </div>

                        {referralUrl ? (
                            <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700 mb-1"><Sparkles className="size-3.5" /> Your personal referral link</div>
                                <CopyRow label="Applicants via this link are tracked as your referral" url={referralUrl} testid={`share-referral-${requisition.id}`} />
                            </div>
                        ) : (
                            <p className="text-xs text-muted-foreground">Your account isn’t linked to an employee record, so no personal referral link is available.</p>
                        )}

                        <button disabled={busy} className="text-xs text-destructive" onClick={() => setPublish(false)} data-testid={`req-unpublish-${requisition.id}`}>
                            Unpublish job
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function CareersBoardButton() {
    const [open, setOpen] = useState(false);
    const [info, setInfo] = useState(null);
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    useEffect(() => {
        if (!open) return;
        api.get("/recruitment/careers-board")
            .then((r) => setInfo(r.data))
            .catch((e) => toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load board"));
    }, [open]);

    const url = info?.careers_token ? `${origin}/careers/company/${info.careers_token}` : "";
    const socials = [
        { key: "facebook", label: "Facebook", href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
        { key: "linkedin", label: "LinkedIn", href: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}` },
        { key: "x", label: "X / Twitter", href: (u) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent("We're hiring! See our open roles:")}` },
    ];

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button className="h-9 px-3 rounded-md bg-secondary text-sm flex items-center gap-2" data-testid="careers-board-btn">
                    <Globe className="size-4" /> Careers Board
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Globe className="size-5" /> Company Careers Board</DialogTitle></DialogHeader>
                {!info ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
                ) : (
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            One public page listing every <span className="font-medium text-foreground">published</span> role. Each role opens its own page to apply; filled roles drop off automatically.
                            {" "}You currently have <span className="font-medium text-foreground">{info.published_count}</span> live posting{info.published_count === 1 ? "" : "s"}.
                        </p>
                        <CopyRow label="Public careers board link" url={url} testid="careers-board-url" />
                        <div>
                            <Label className="text-xs">Share to social media</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {socials.map((s) => (
                                    <a key={s.key} href={s.href(url)} target="_blank" rel="noreferrer"
                                        className="h-9 px-3 rounded-md bg-secondary text-sm inline-flex items-center gap-1" data-testid={`board-social-${s.key}`}>
                                        <Share2 className="size-3.5" /> {s.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary">
                            <Globe className="size-4" /> Open careers board
                        </a>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

const WORK_ARRANGEMENTS = [["onsite", "On-site"], ["hybrid", "Hybrid"], ["remote", "Remote"]];
const EMPLOYMENT_TYPES = [["full_time", "Full-time"], ["part_time", "Part-time"], ["contract", "Contract"], ["project_based", "Project-based"], ["internship", "Internship"]];
const HIRING_REASONS = [["new_headcount", "New position"], ["replacement", "Replacement"]];
const PRIORITIES = [["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]];

export default function Recruitment() {
    const [dashboard, setDashboard] = useState(null);
    const [requisitions, setRequisitions] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [offers, setOffers] = useState([]);
    const [openReq, setOpenReq] = useState(false);
    const [openCand, setOpenCand] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [offerForm, setOfferForm] = useState({
        title: "",
        salary: "",
        salary_frequency: "monthly",
        start_date: "",
        work_location: "",
        benefits: "HMO, 13th Month, Government Benefits",
        probation_months: 6,
        notes: "",
    });

    const [reqForm, setReqForm] = useState({
        title: "",
        department: "",
        reason: "new_headcount",
        employment_type: "full_time",
        location: "Metro Manila",
        remote_type: "onsite",
        headcount: 1,
        salary_min: "",
        salary_max: "",
        priority: "normal",
        description: "",
        required_skills: "",
        preferred_skills: "",
        status: "draft",
    });

    const [candForm, setCandForm] = useState({
        requisition_id: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        current_company: "",
        current_title: "",
        location: "",
        source: "manual",
        stage: "applied",
        skills: "",
        years_experience: "",
        expected_salary: "",
        availability: "",
    });

    const [analytics, setAnalytics] = useState(null);

    const load = async () => {
        const [d, r, c, o, a] = await Promise.all([
            api.get("/recruitment/dashboard"),
            api.get("/recruitment/requisitions"),
            api.get("/recruitment/candidates"),
            api.get("/recruitment/offers").catch(() => ({ data: { items: [] } })),
            api.get("/recruitment/analytics").catch(() => ({ data: null })),
        ]);
        setDashboard(d.data);
        setRequisitions(r.data.items || []);
        setCandidates(c.data.items || []);
        setOffers(o.data.items || []);
        setAnalytics(a.data);
    };

    useEffect(() => { load(); }, []);

    const reqAction = async (reqId, action) => {
        try { await api.post(`/recruitment/requisitions/${reqId}/${action}`); toast.success(`Requisition ${action}d`); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const setArrangement = async (r, value) => {
        try {
            await api.patch(`/recruitment/requisitions/${r.id}`, { remote_type: value });
            toast.success(`${r.title} is now ${WORK_ARRANGEMENTS.find(([v]) => v === value)?.[1] || value}${r.is_published ? ", and the job board shows it" : ""}`);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not change it"); }
    };

    const byReq = useMemo(() => {
        const map = {};
        for (const r of requisitions) map[r.id] = r;
        return map;
    }, [requisitions]);

    const selectedOffer = useMemo(() => {
        if (!selectedCandidate) return null;
        return offers.find((o) => o.candidate_id === selectedCandidate.id);
    }, [offers, selectedCandidate]);

    const refreshSelected = async (id) => {
        const fresh = (await api.get("/recruitment/candidates")).data.items?.find((x) => x.id === id);
        if (fresh) setSelectedCandidate(fresh);
    };

    const createReq = async () => {
        try {
            await api.post("/recruitment/requisitions", {
                ...reqForm,
                headcount: Number(reqForm.headcount || 1),
                salary_min: reqForm.salary_min ? Number(reqForm.salary_min) : null,
                salary_max: reqForm.salary_max ? Number(reqForm.salary_max) : null,
                required_skills: reqForm.required_skills.split(",").map((x) => x.trim()).filter(Boolean),
                preferred_skills: reqForm.preferred_skills.split(",").map((x) => x.trim()).filter(Boolean),
            });
            toast.success("Requisition created");
            setOpenReq(false);
            load();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const createCand = async () => {
        try {
            await api.post("/recruitment/candidates", {
                ...candForm,
                skills: candForm.skills.split(",").map((x) => x.trim()).filter(Boolean),
                years_experience: candForm.years_experience ? Number(candForm.years_experience) : null,
                expected_salary: candForm.expected_salary ? Number(candForm.expected_salary) : null,
            });
            toast.success("Candidate added");
            setOpenCand(false);
            load();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const moveStage = async (id, stage) => {
        try {
            await api.post(`/recruitment/candidates/${id}/move`, { stage });
            toast.success(`Moved to ${titleCase(stage)}`);
            await load();
            if (selectedCandidate?.id === id) setSelectedCandidate({ ...selectedCandidate, stage });
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const addNote = async (id) => {
        const note = window.prompt("Add candidate note");
        if (!note) return;
        try {
            await api.post(`/recruitment/candidates/${id}/notes`, { note });
            toast.success("Note added");
            await load();
            if (selectedCandidate?.id === id) await refreshSelected(id);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    // AI-interview sessions + email previews for the candidate drawer
    const [interviewSessions, setInterviewSessions] = useState([]);
    const [emailSim, setEmailSim] = useState(null);
    useEffect(() => {
        api.get("/ai-interview/sessions").then((r) => setInterviewSessions(r.data.items || [])).catch(() => {});
    }, []);
    const openEmailSim = async () => {
        try {
            const { data } = await api.get(`/recruitment/candidates/${selectedCandidate.id}/email-previews`);
            setEmailSim(data);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const candSessions = selectedCandidate ? interviewSessions.filter((s) => s.candidate_id === selectedCandidate.id) : [];

    const uploadResume = async (candidateId, file) => {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        try {
            await api.post(`/recruitment/candidates/${candidateId}/resume`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Resume uploaded and parsed");
            await load();
            if (selectedCandidate?.id === candidateId) await refreshSelected(candidateId);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const resetOfferFormFromCandidate = (c) => {
        const req = byReq[c?.requisition_id];
        setOfferForm({
            title: req?.title || c?.current_title || "",
            salary: c?.expected_salary || "",
            salary_frequency: "monthly",
            start_date: "",
            work_location: req?.location || c?.location || "",
            benefits: "HMO, 13th Month, Government Benefits",
            probation_months: 6,
            notes: "",
        });
    };

    const createOffer = async () => {
        if (!selectedCandidate) return;
        try {
            await api.post(`/recruitment/candidates/${selectedCandidate.id}/offer`, {
                ...offerForm,
                salary: Number(offerForm.salary || 0),
                probation_months: Number(offerForm.probation_months || 6),
                benefits: offerForm.benefits.split(",").map((x) => x.trim()).filter(Boolean),
            });
            toast.success("Offer created");
            await load();
            await refreshSelected(selectedCandidate.id);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const acceptOffer = async () => {
        if (!selectedOffer) return;
        try {
            await api.post(`/recruitment/offers/${selectedOffer.id}/accept`, {
                acceptance_notes: "Offer accepted from ATS.",
            });
            toast.success("Offer accepted");
            await load();
            await refreshSelected(selectedCandidate.id);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const hireOffer = async () => {
        if (!selectedOffer) return;
        try {
            const { data } = await api.post(`/recruitment/offers/${selectedOffer.id}/hire`);
            toast.success(`Candidate hired as ${data.employee.employee_no}`);
            await load();
            await refreshSelected(selectedCandidate.id);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-5">
            <PageHeader
                title="Recruitment ATS"
                subtitle="Job requisitions, candidate pipeline, and hiring funnel."
                actions={
                    <>
                        <CareersBoardButton />
                        <Dialog open={openReq} onOpenChange={setOpenReq}>
                            <DialogTrigger asChild>
                                <button className="h-9 px-3 rounded-md bg-secondary text-sm flex items-center gap-2">
                                    <Plus className="size-4" /> New Requisition
                                </button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader><DialogTitle>New Job Requisition</DialogTitle></DialogHeader>
                                <div className="grid gap-3 md:grid-cols-2">
                                    <div><Label>Title</Label><Input value={reqForm.title} onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })} /></div>
                                    <div><Label>Department</Label><Input value={reqForm.department} onChange={(e) => setReqForm({ ...reqForm, department: e.target.value })} /></div>
                                    <div><Label>Location</Label><Input value={reqForm.location} onChange={(e) => setReqForm({ ...reqForm, location: e.target.value })} /></div>
                                    <div>
                                        <Label>Work arrangement</Label>
                                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={reqForm.remote_type} onChange={(e) => setReqForm({ ...reqForm, remote_type: e.target.value })} data-testid="req-remote-type">
                                            {WORK_ARRANGEMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label>Employment type</Label>
                                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={reqForm.employment_type} onChange={(e) => setReqForm({ ...reqForm, employment_type: e.target.value })} data-testid="req-employment-type">
                                            {EMPLOYMENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label>Reason for hiring</Label>
                                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={reqForm.reason} onChange={(e) => setReqForm({ ...reqForm, reason: e.target.value })} data-testid="req-reason">
                                            {HIRING_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </div>
                                    <div><Label>Headcount</Label><Input type="number" value={reqForm.headcount} onChange={(e) => setReqForm({ ...reqForm, headcount: e.target.value })} /></div>
                                    <div><Label>Salary Min</Label><Input type="number" value={reqForm.salary_min} onChange={(e) => setReqForm({ ...reqForm, salary_min: e.target.value })} /></div>
                                    <div><Label>Salary Max</Label><Input type="number" value={reqForm.salary_max} onChange={(e) => setReqForm({ ...reqForm, salary_max: e.target.value })} /></div>
                                    <div>
                                        <Label>Priority</Label>
                                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={reqForm.priority} onChange={(e) => setReqForm({ ...reqForm, priority: e.target.value })} data-testid="req-priority">
                                            {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </div>
                                    <div><Label>Status</Label><Input value={reqForm.status} onChange={(e) => setReqForm({ ...reqForm, status: e.target.value })} /></div>
                                    <div className="md:col-span-2"><Label>Required Skills, comma-separated</Label><Input value={reqForm.required_skills} onChange={(e) => setReqForm({ ...reqForm, required_skills: e.target.value })} /></div>
                                    <div className="md:col-span-2"><Label>Description</Label><Input value={reqForm.description} onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} /></div>
                                </div>
                                <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={createReq}>Create</button></DialogFooter>
                            </DialogContent>
                        </Dialog>

                        <Dialog open={openCand} onOpenChange={setOpenCand}>
                            <DialogTrigger asChild>
                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-2">
                                    <Plus className="size-4" /> New Candidate
                                </button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader><DialogTitle>New Candidate</DialogTitle></DialogHeader>
                                <div className="grid gap-3 md:grid-cols-2">
                                    <div><Label>First Name</Label><Input value={candForm.first_name} onChange={(e) => setCandForm({ ...candForm, first_name: e.target.value })} /></div>
                                    <div><Label>Last Name</Label><Input value={candForm.last_name} onChange={(e) => setCandForm({ ...candForm, last_name: e.target.value })} /></div>
                                    <div><Label>Email</Label><Input value={candForm.email} onChange={(e) => setCandForm({ ...candForm, email: e.target.value })} /></div>
                                    <div><Label>Phone</Label><Input value={candForm.phone} onChange={(e) => setCandForm({ ...candForm, phone: e.target.value })} /></div>
                                    <div><Label>Current Company</Label><Input value={candForm.current_company} onChange={(e) => setCandForm({ ...candForm, current_company: e.target.value })} /></div>
                                    <div><Label>Current Title</Label><Input value={candForm.current_title} onChange={(e) => setCandForm({ ...candForm, current_title: e.target.value })} /></div>
                                    <div>
                                        <Label>Requisition</Label>
                                        <select className="w-full h-10 px-3 rounded-md border border-border" value={candForm.requisition_id} onChange={(e) => setCandForm({ ...candForm, requisition_id: e.target.value })}>
                                            <option value="">Unassigned</option>
                                            {requisitions.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                                        </select>
                                    </div>
                                    <div><Label>Source</Label><Input value={candForm.source} onChange={(e) => setCandForm({ ...candForm, source: e.target.value })} /></div>
                                    <div><Label>Skills, comma-separated</Label><Input value={candForm.skills} onChange={(e) => setCandForm({ ...candForm, skills: e.target.value })} /></div>
                                    <div><Label>Expected Salary</Label><Input type="number" value={candForm.expected_salary} onChange={(e) => setCandForm({ ...candForm, expected_salary: e.target.value })} /></div>
                                </div>
                                <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={createCand}>Create</button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </>
                }
            />

            <div className="grid gap-3 md:grid-cols-5">
                <Stat label="Open Positions" value={dashboard?.open_requisitions || 0} icon={BriefcaseBusiness} tone="primary" />
                <Stat label="Candidates" value={dashboard?.total_candidates || 0} icon={UsersRound} />
                <Stat label="Screening" value={dashboard?.pipeline?.screening || 0} icon={UserRound} />
                <Stat label="Interviews" value={dashboard?.pipeline?.interview || 0} icon={CalendarDays} />
                <Stat label="Hired" value={dashboard?.hired || 0} icon={CheckCircle2} />
            </div>

            <Tabs defaultValue="pipeline">
                <TabsList>
                    <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
                    <TabsTrigger value="requisitions">Requisitions</TabsTrigger>
                    <TabsTrigger value="candidates">Candidates</TabsTrigger>
                    <TabsTrigger value="offers">Offers</TabsTrigger>
                    <TabsTrigger value="analytics" data-testid="rec-tab-analytics">Analytics</TabsTrigger>
                </TabsList>

                <TabsContent value="analytics">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-3">
                        <Stat label="Total Candidates" value={analytics?.total_candidates || 0} tone="primary" />
                        <Stat label="Avg Time to Hire" value={analytics?.avg_time_to_hire_days != null ? `${analytics.avg_time_to_hire_days}d` : "—"} />
                        <Stat label="Offer Acceptance" value={analytics?.offer_acceptance_rate != null ? `${Math.round(analytics.offer_acceptance_rate * 100)}%` : "—"} />
                        <Stat label="Applied → Hire" value={analytics?.applied_to_hire_rate != null ? `${Math.round(analytics.applied_to_hire_rate * 100)}%` : "—"} />
                    </div>
                    <Surface className="p-4">
                        <h3 className="font-semibold text-sm mb-3">Pipeline Funnel</h3>
                        <div className="space-y-2">
                            {STAGES.map((s) => {
                                const n = analytics?.pipeline?.[s] || 0;
                                const max = Math.max(1, ...Object.values(analytics?.pipeline || { x: 1 }));
                                return (
                                    <div key={s} className="flex items-center gap-3">
                                        <div className="w-28 text-xs capitalize text-muted-foreground">{titleCase(s)}</div>
                                        <div className="flex-1 h-5 rounded bg-secondary overflow-hidden"><div className="h-full bg-primary/70" style={{ width: `${(n / max) * 100}%` }} /></div>
                                        <div className="w-8 text-right text-sm font-mono">{n}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </Surface>
                </TabsContent>

                <TabsContent value="pipeline">
                    {/* Columns keep a readable width; the board itself scrolls sideways. */}
                    <div className="flex gap-3 overflow-x-auto pb-3">
                        {STAGES.map((stage) => {
                            const col = candidates.filter((c) => c.stage === stage);
                            return (
                                <Surface key={stage} className="p-4 min-h-[520px] w-[260px] shrink-0 bg-slate-50/40 rounded-2xl">
                                    <div className="mb-3 flex items-center justify-between">
                                        <div className={`text-xs uppercase tracking-wider font-bold border rounded-full px-3 py-1 ${stageTone[stage]}`}>{titleCase(stage)}</div>
                                        <div className="text-xs font-mono text-muted-foreground">{col.length}</div>
                                    </div>

                                    <div className="space-y-3">
                                        {col.map((c) => (
                                            <CandidateCard
                                                key={c.id}
                                                c={c}
                                                job={byReq[c.requisition_id]}
                                                openProfile={(c) => { setSelectedCandidate(c); resetOfferFormFromCandidate(c); }}
                                                onMove={moveStage}
                                            />
                                        ))}

                                        {!col.length && (
                                            <div className="h-72 rounded-2xl border border-dashed flex flex-col items-center justify-center text-center text-xs text-muted-foreground p-5 bg-white/70">
                                                <FileText className="size-8 mb-2 opacity-50" />
                                                <div>No candidates</div>
                                                <div className="mt-1">Move candidates here as they progress.</div>
                                            </div>
                                        )}
                                    </div>
                                </Surface>
                            );
                        })}
                    </div>
                </TabsContent>

                <TabsContent value="requisitions">
                    <TableWrap>
                        <thead><tr><th>Position</th><th>Department</th><th>Work arrangement</th><th>Headcount</th><th>Salary Range</th><th>Priority</th><th>Status</th><th>Created</th><th>Approval</th></tr></thead>
                        <tbody>
                            {requisitions.map((r) => (
                                <tr key={r.id}>
                                    <td className="font-medium">{r.title}</td>
                                    <td>{r.department}</td>
                                    <td>
                                        <select className="h-8 rounded-md border border-border bg-background px-1 text-xs" value={r.remote_type || "onsite"} onChange={(e) => setArrangement(r, e.target.value)} data-testid={`req-arrangement-${r.id}`} aria-label={`Work arrangement for ${r.title}`}>
                                            {WORK_ARRANGEMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </td>
                                    <td>{r.headcount}</td>
                                    <td>{formatPHP(r.salary_min)} – {formatPHP(r.salary_max)}</td>
                                    <td><StatusBadge value={r.priority} /></td>
                                    <td><StatusBadge value={r.status} /></td>
                                    <td>{formatDate(r.created_at)}</td>
                                    <td>
                                        <div className="flex items-center gap-3">
                                            {r.status === "draft" && <button className="text-xs text-primary" onClick={() => reqAction(r.id, "submit")}>Submit</button>}
                                            {r.status === "submitted" && <>
                                                <button className="text-xs text-emerald-600" onClick={() => reqAction(r.id, "approve")} data-testid={`req-approve-${r.id}`}>Approve</button>
                                                <button className="text-xs text-destructive" onClick={() => reqAction(r.id, "reject")}>Reject</button>
                                            </>}
                                            <ShareDialog requisition={r} onChanged={load} />
                                            {r.is_published && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Live</span>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!requisitions.length && <tr><td colSpan={9}><Empty title="No requisitions" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>

                <TabsContent value="candidates">
                    <TableWrap>
                        <thead><tr><th>Name</th><th>Email</th><th>Position</th><th>Stage</th><th>Resume Match</th><th>Source</th><th>Expected Salary</th><th>Created</th></tr></thead>
                        <tbody>
                            {candidates.map((c) => {
                                const matched = isMatched(c);
                                return (
                                <tr key={c.id} className={matched ? "bg-emerald-50/50" : ""}>
                                    <td className="font-medium">
                                        <button className="text-primary hover:underline inline-flex items-center gap-1.5" onClick={() => { setSelectedCandidate(c); resetOfferFormFromCandidate(c); }}>
                                            {c.first_name} {c.last_name}
                                            {matched && <CheckCircle2 className="size-3.5 text-emerald-600" />}
                                        </button>
                                    </td>
                                    <td>{c.email}</td>
                                    <td>{byReq[c.requisition_id]?.title || "Unassigned"}</td>
                                    <td><StatusBadge value={c.stage} /></td>
                                    <td>
                                        {c.resume_match
                                            ? <span className={`font-medium ${matched ? "text-emerald-700" : "text-muted-foreground"}`}>{c.resume_match.match_score ?? 0}%</span>
                                            : "—"}
                                    </td>
                                    <td><SourceBadge c={c} /> {(!c.source || c.source === "manual") && <span className="text-muted-foreground">manual</span>}</td>
                                    <td>{c.expected_salary ? formatPHP(c.expected_salary) : "—"}</td>
                                    <td>{formatDate(c.created_at)}</td>
                                </tr>
                                );
                            })}
                            {!candidates.length && <tr><td colSpan={8}><Empty title="No candidates" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>

                <TabsContent value="offers">
                    <TableWrap>
                        <thead><tr><th>Candidate</th><th>Title</th><th>Salary</th><th>Start Date</th><th>Status</th><th>Created</th></tr></thead>
                        <tbody>
                            {offers.map((o) => {
                                const c = candidates.find((x) => x.id === o.candidate_id);
                                return (
                                    <tr key={o.id}>
                                        <td>{c ? `${c.first_name} ${c.last_name}` : o.candidate_id}</td>
                                        <td>{o.title}</td>
                                        <td>{formatPHP(o.salary)} / {o.salary_frequency}</td>
                                        <td>{formatDate(o.start_date)}</td>
                                        <td><StatusBadge value={o.status} /></td>
                                        <td>{formatDate(o.created_at)}</td>
                                    </tr>
                                );
                            })}
                            {!offers.length && <tr><td colSpan={6}><Empty title="No offers yet" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>
            </Tabs>

            <Dialog open={!!selectedCandidate} onOpenChange={(v) => !v && setSelectedCandidate(null)}>
                <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
                    {selectedCandidate && (
                        <>
                            <div className="flex items-start justify-between gap-4">
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-3">
                                        <span className="size-12 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-sm font-bold">
                                            {initials(selectedCandidate)}
                                        </span>
                                        <span>
                                            <span className="block">{selectedCandidate.first_name} {selectedCandidate.last_name}</span>
                                            <span className="text-sm font-normal text-muted-foreground">{selectedCandidate.current_title || "—"} · {selectedCandidate.current_company || "—"}</span>
                                        </span>
                                    </DialogTitle>
                                </DialogHeader>
                                <button onClick={() => setSelectedCandidate(null)} className="p-1 rounded hover:bg-secondary"><X className="size-4" /></button>
                            </div>

                            <div className="grid gap-4 md:grid-cols-3">
                                <Surface className="p-4 md:col-span-1 space-y-3">
                                    <div className="flex items-center gap-2 text-sm"><Mail className="size-4 text-muted-foreground" /> {selectedCandidate.email || "—"}</div>
                                    <div className="flex items-center gap-2 text-sm"><Phone className="size-4 text-muted-foreground" /> {selectedCandidate.phone || "—"}</div>
                                    <div className="flex items-center gap-2 text-sm"><MapPin className="size-4 text-muted-foreground" /> {selectedCandidate.location || "—"}</div>

                                    <div>
                                        <div className="text-xs text-muted-foreground">Requisition</div>
                                        <div className="font-medium">{byReq[selectedCandidate.requisition_id]?.title || "Unassigned"}</div>
                                    </div>

                                    <div>
                                        <div className="text-xs text-muted-foreground">Current Stage</div>
                                        <select
                                            className="mt-1 w-full h-9 text-sm rounded border border-border"
                                            value={selectedCandidate.stage}
                                            onChange={async (e) => {
                                                await moveStage(selectedCandidate.id, e.target.value);
                                                setSelectedCandidate({ ...selectedCandidate, stage: e.target.value });
                                            }}
                                        >
                                            {STAGES.map((x) => <option key={x} value={x}>{titleCase(x)}</option>)}
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 pt-2">
                                        <label className="h-9 px-3 rounded-md bg-secondary text-sm cursor-pointer inline-flex items-center justify-center gap-2">
                                            <Upload className="size-4" />
                                            Resume
                                            <input
                                                type="file"
                                                className="hidden"
                                                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                                                onChange={(e) => uploadResume(selectedCandidate.id, e.target.files?.[0])}
                                            />
                                        </label>
                                        <button className="h-9 px-3 rounded-md bg-secondary text-sm" onClick={() => addNote(selectedCandidate.id)}>Add Note</button>
                                    </div>
                                </Surface>

                                <Surface className="p-4 md:col-span-2">
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold">Resume Match</div>
                                        <div className="font-mono text-2xl font-bold">{selectedCandidate.resume_match?.match_score ?? 0}%</div>
                                    </div>

                                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                                        <div>
                                            <div className="text-xs uppercase text-muted-foreground font-semibold">Matched Required</div>
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {(selectedCandidate.resume_match?.matched_required_skills || []).map((x) => <SkillPill key={x} tone="good">{x}</SkillPill>)}
                                                {!selectedCandidate.resume_match?.matched_required_skills?.length && <span className="text-sm text-muted-foreground">None yet</span>}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-muted-foreground font-semibold">Missing Required</div>
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {(selectedCandidate.resume_match?.missing_required_skills || []).map((x) => <SkillPill key={x} tone="warn">{x}</SkillPill>)}
                                                {!selectedCandidate.resume_match?.missing_required_skills?.length && <span className="text-sm text-muted-foreground">None</span>}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-muted-foreground font-semibold">Matched Preferred</div>
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {(selectedCandidate.resume_match?.matched_preferred_skills || []).map((x) => <SkillPill key={x} tone="blue">{x}</SkillPill>)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-muted-foreground font-semibold">Detected Skills</div>
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {(selectedCandidate.resume_match?.skills_found || selectedCandidate.skills || []).map((x) => <SkillPill key={x}>{x}</SkillPill>)}
                                            </div>
                                        </div>
                                    </div>
                                </Surface>

                                <Surface className="p-4 md:col-span-3">
                                    <div className="flex items-center justify-between gap-3 mb-3">
                                        <div>
                                            <div className="font-semibold">Offer Management</div>
                                            <div className="text-xs text-muted-foreground">Create offer, accept, then convert candidate to employee.</div>
                                        </div>
                                        {selectedOffer && <StatusBadge value={selectedOffer.status} />}
                                    </div>

                                    {selectedOffer ? (
                                        <div className="space-y-4">
                                            <div className="grid gap-3 md:grid-cols-4 text-sm">
                                                <div><div className="text-xs text-muted-foreground">Title</div>{selectedOffer.title}</div>
                                                <div><div className="text-xs text-muted-foreground">Salary</div>{formatPHP(selectedOffer.salary)} / {selectedOffer.salary_frequency}</div>
                                                <div><div className="text-xs text-muted-foreground">Start Date</div>{formatDate(selectedOffer.start_date)}</div>
                                                <div><div className="text-xs text-muted-foreground">Location</div>{selectedOffer.work_location || "—"}</div>
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                {selectedOffer.status === "pending" && (
                                                    <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={acceptOffer}>
                                                        Accept Offer
                                                    </button>
                                                )}
                                                {["pending", "accepted"].includes(selectedOffer.status) && (
                                                    <button className="h-9 px-3 rounded-md bg-emerald-600 text-white text-sm" onClick={hireOffer}>
                                                        Hire / Convert to Employee
                                                    </button>
                                                )}
                                                {selectedOffer.employee_id && (
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        <div className="text-sm text-muted-foreground">
                                                            Employee ID: <span className="font-mono ml-1">{selectedOffer.employee_id}</span>
                                                        </div>
                                                        <Link
                                                            to={`/onboarding?employee_id=${selectedOffer.employee_id}`}
                                                            className="h-9 px-3 rounded-md bg-secondary text-sm inline-flex items-center justify-center"
                                                        >
                                                            Open Onboarding
                                                        </Link>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid gap-3 md:grid-cols-3">
                                            <div><Label>Offer Title</Label><Input value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} /></div>
                                            <div><Label>Monthly Salary</Label><Input type="number" value={offerForm.salary} onChange={(e) => setOfferForm({ ...offerForm, salary: e.target.value })} /></div>
                                            <div><Label>Start Date</Label><Input type="date" value={offerForm.start_date} onChange={(e) => setOfferForm({ ...offerForm, start_date: e.target.value })} /></div>
                                            <div><Label>Work Location</Label><Input value={offerForm.work_location} onChange={(e) => setOfferForm({ ...offerForm, work_location: e.target.value })} /></div>
                                            <div><Label>Probation Months</Label><Input type="number" value={offerForm.probation_months} onChange={(e) => setOfferForm({ ...offerForm, probation_months: e.target.value })} /></div>
                                            <div><Label>Benefits</Label><Input value={offerForm.benefits} onChange={(e) => setOfferForm({ ...offerForm, benefits: e.target.value })} /></div>
                                            <div className="md:col-span-3"><Label>Notes</Label><Input value={offerForm.notes} onChange={(e) => setOfferForm({ ...offerForm, notes: e.target.value })} /></div>
                                            <div className="md:col-span-3">
                                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={createOffer}>
                                                    Create Offer
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </Surface>

                                <Surface className="p-4 md:col-span-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-semibold">HR Notes</div>
                                        <div className="flex gap-2">
                                            <button className="h-8 px-2.5 rounded-md border border-border text-xs" onClick={() => addNote(selectedCandidate.id)} data-testid="drawer-add-note">+ Add note</button>
                                            <button className="h-8 px-2.5 rounded-md border border-primary/50 text-primary text-xs" onClick={openEmailSim} data-testid="email-sim-btn">📧 Email Simulator</button>
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        {(selectedCandidate.timeline || []).filter((t) => t.type === "note").slice().reverse().map((t) => (
                                            <div key={t.id} className="rounded-md border border-amber-200 bg-amber-50/60 p-2.5">
                                                <div className="text-sm">{t.message}</div>
                                                <div className="text-[10px] text-muted-foreground mt-0.5">{t.at}</div>
                                            </div>
                                        ))}
                                        {!(selectedCandidate.timeline || []).some((t) => t.type === "note") && <div className="text-xs text-muted-foreground">No HR notes yet — add screening remarks, salary expectations, red flags…</div>}
                                    </div>
                                </Surface>

                                <Surface className="p-4 md:col-span-3">
                                    <div className="font-semibold mb-2">🎥 Video Interview Results</div>
                                    <div className="space-y-1.5">
                                        {candSessions.map((s) => (
                                            <div key={s.id} className="rounded-md border border-border p-2.5 flex items-center justify-between gap-2 flex-wrap" data-testid={`drawer-session-${s.id}`}>
                                                <div>
                                                    <div className="text-sm font-medium">{s.kit_title}</div>
                                                    <div className="text-[11px] text-muted-foreground">
                                                        {s.status === "completed" ? `Completed ${formatDate(s.completed_at)}` : `Invited ${formatDate(s.invited_at)} — awaiting answer`}
                                                        {s.engagement && ` · video played ${s.engagement.video_plays}× · answered after ${Math.round((s.engagement.prep_seconds || 0) / 60)}m prep`}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {s.analysis && (
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.analysis.verdict === "likely_reading" ? "bg-red-100 text-red-800 border border-red-300" : s.analysis.verdict === "inconclusive" ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-emerald-100 text-emerald-800 border border-emerald-300"}`}>
                                                            {s.analysis.reading_score} · {s.analysis.verdict.replace(/_/g, " ")}
                                                        </span>
                                                    )}
                                                    <button className="h-7 px-2 text-xs rounded-md border border-border" onClick={() => window.open("/recruitment/ai-interview", "_self")}>Open review →</button>
                                                </div>
                                            </div>
                                        ))}
                                        {!candSessions.length && <div className="text-xs text-muted-foreground">No AI interview yet — invite this candidate from Recruitment → AI Interview.</div>}
                                    </div>
                                </Surface>

                                <Surface className="p-4 md:col-span-3">
                                    <div className="font-semibold mb-2">Timeline</div>
                                    <div className="space-y-2">
                                        {(selectedCandidate.timeline || []).slice().reverse().map((t) => (
                                            <div key={t.id} className="rounded-md border p-3">
                                                <div className="text-sm font-medium">{t.message}</div>
                                                <div className="text-xs text-muted-foreground">{titleCase(t.type)} · {t.at}</div>
                                            </div>
                                        ))}
                                        {!selectedCandidate.timeline?.length && <Empty title="No timeline events" />}
                                    </div>
                                </Surface>

                                <Surface className="p-4 md:col-span-3">
                                    <div className="font-semibold mb-2">Resume Text Preview</div>
                                    <div className="max-h-64 overflow-auto rounded-md bg-secondary p-3 text-xs whitespace-pre-wrap">
                                        {selectedCandidate.resume_text || "No resume text extracted yet."}
                                    </div>
                                </Surface>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Email Simulator — what the candidate receives at every hiring step */}
            <Dialog open={!!emailSim} onOpenChange={(v) => !v && setEmailSim(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>📧 Candidate Email Simulator — {emailSim?.candidate}</DialogTitle></DialogHeader>
                    {emailSim && (
                        <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
                            <div className="text-xs text-muted-foreground">
                                To: <b>{emailSim.to || "no email on file"}</b> · current stage: <span className="badge badge-info">{emailSim.current_stage}</span>
                                {emailSim.interview_link
                                    ? <> · AI interview link: <a className="text-primary underline break-all" href={emailSim.interview_link} target="_blank" rel="noreferrer">{emailSim.interview_link}</a></>
                                    : " · no AI interview link yet (generate one via Invite candidate)"}
                                <div className="mt-1">These exact emails are sent automatically whenever the candidate moves stage (queued until SMTP is configured).</div>
                            </div>
                            {emailSim.emails.map((e) => (
                                <div key={e.stage} className={`rounded-md border p-3 ${e.stage === emailSim.current_stage ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`sim-${e.stage}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-1 mb-1">
                                        <span className="badge badge-draft uppercase text-[9px]">{e.stage}</span>
                                        {e.stage === emailSim.current_stage && <span className="text-[10px] text-primary font-semibold">← current stage</span>}
                                    </div>
                                    <div className="text-sm font-semibold">{e.subject}</div>
                                    <pre className="text-xs text-muted-foreground whitespace-pre-wrap mt-1 font-sans">{e.body}</pre>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
